// 🔧 content_types.js
// ✅ Purpose: Defines canonical Content Engine content types
// ✅ Features:
// - Supports editable text content, page content, SEO, emails, notifications, forms, media, images, and accessibility metadata
// - Prepares the Content Engine for localization, AI-assisted writing, accessibility review, and future white-label organizations
// - Keeps all content type values centralized so pages, admin tools, resolvers, and bundle builders do not hardcode strings

export const CONTENT_TYPES = {
  // Page identity content
  PAGE_TITLE: "page_title",
  PAGE_SUBTITLE: "page_subtitle",
  PAGE_DESCRIPTION: "page_description",

  // Headings and structure
  HEADING: "heading",
  SUBHEADING: "subheading",
  SECTION_TITLE: "section_title",
  CARD_TITLE: "card_title",

  // Body/content blocks
  BODY_TEXT: "body_text",
  RICH_TEXT: "rich_text",
  CONTENT_BLOCK: "content_block",
  EMPTY_STATE: "empty_state",
  INSTRUCTION_TEXT: "instruction_text",
  DISCLAIMER: "disclaimer",

  // Actions/navigation
  BUTTON_LABEL: "button_label",
  LINK_LABEL: "link_label",
  NAV_LABEL: "nav_label",
  CTA_TEXT: "cta_text",

  // Forms
  FORM_LABEL: "form_label",
  FORM_PLACEHOLDER: "form_placeholder",
  FORM_HELP_TEXT: "form_help_text",
  VALIDATION_MESSAGE: "validation_message",

  // Notifications
  NOTIFICATION_SUCCESS: "notification_success",
  NOTIFICATION_ERROR: "notification_error",
  NOTIFICATION_WARNING: "notification_warning",
  NOTIFICATION_INFO: "notification_info",

  // SEO/social metadata
  SEO_TITLE: "seo_title",
  SEO_DESCRIPTION: "seo_description",
  SEO_KEYWORDS: "seo_keywords",
  SEO_OG_TITLE: "seo_og_title",
  SEO_OG_DESCRIPTION: "seo_og_description",
  SEO_SCHEMA_DESCRIPTION: "seo_schema_description",
  SEO_IMAGE: "seo_image",

  // Email content
  EMAIL_SUBJECT: "email_subject",
  EMAIL_PREHEADER: "email_preheader",
  EMAIL_HEADING: "email_heading",
  EMAIL_BODY: "email_body",
  EMAIL_BUTTON_LABEL: "email_button_label",
  EMAIL_FOOTER: "email_footer",
  EMAIL_IMAGE: "email_image",

  // Admin/manager guidance
  ADMIN_HELP_TEXT: "admin_help_text",
  ADMIN_WARNING: "admin_warning",
  ADMIN_SETUP_PROMPT: "admin_setup_prompt",

  // Legal/trust content
  LEGAL_TEXT: "legal_text",
  TRUST_TEXT: "trust_text",

  // System vocabulary
  VOCABULARY: "vocabulary",

  // Media/image content
  IMAGE: "image",
  IMAGE_ALT_TEXT: "image_alt_text",
  IMAGE_CAPTION: "image_caption",
  IMAGE_TITLE: "image_title",
  IMAGE_DESCRIPTION: "image_description",
  ICON_LABEL: "icon_label",
  MEDIA_BLOCK: "media_block",

  // ============================================================
  // 📧 Email Content Types
  // ============================================================

  EMAIL_SUBJECT: "email_subject",
  EMAIL_HEADING: "email_heading",
  EMAIL_HTML: "email_html",
  EMAIL_BODY: "email_body",
  EMAIL_BUTTON_LABEL: "email_button_label",
  EMAIL_FOOTER: "email_footer",

};

export const CONTENT_TYPE_LABELS = {
  [CONTENT_TYPES.PAGE_TITLE]: "Page Title",
  [CONTENT_TYPES.PAGE_SUBTITLE]: "Page Subtitle",
  [CONTENT_TYPES.PAGE_DESCRIPTION]: "Page Description",

  [CONTENT_TYPES.HEADING]: "Heading",
  [CONTENT_TYPES.SUBHEADING]: "Subheading",
  [CONTENT_TYPES.SECTION_TITLE]: "Section Title",
  [CONTENT_TYPES.CARD_TITLE]: "Card Title",

  [CONTENT_TYPES.BODY_TEXT]: "Body Text",
  [CONTENT_TYPES.RICH_TEXT]: "Rich Text",
  [CONTENT_TYPES.CONTENT_BLOCK]: "Content Block",
  [CONTENT_TYPES.EMPTY_STATE]: "Empty State",
  [CONTENT_TYPES.INSTRUCTION_TEXT]: "Instruction Text",
  [CONTENT_TYPES.DISCLAIMER]: "Disclaimer",

  [CONTENT_TYPES.BUTTON_LABEL]: "Button Label",
  [CONTENT_TYPES.LINK_LABEL]: "Link Label",
  [CONTENT_TYPES.NAV_LABEL]: "Navigation Label",
  [CONTENT_TYPES.CTA_TEXT]: "Call To Action",

  [CONTENT_TYPES.FORM_LABEL]: "Form Label",
  [CONTENT_TYPES.FORM_PLACEHOLDER]: "Form Placeholder",
  [CONTENT_TYPES.FORM_HELP_TEXT]: "Form Help Text",
  [CONTENT_TYPES.VALIDATION_MESSAGE]: "Validation Message",

  [CONTENT_TYPES.NOTIFICATION_SUCCESS]: "Success Notification",
  [CONTENT_TYPES.NOTIFICATION_ERROR]: "Error Notification",
  [CONTENT_TYPES.NOTIFICATION_WARNING]: "Warning Notification",
  [CONTENT_TYPES.NOTIFICATION_INFO]: "Info Notification",

  [CONTENT_TYPES.SEO_TITLE]: "SEO Title",
  [CONTENT_TYPES.SEO_DESCRIPTION]: "SEO Description",
  [CONTENT_TYPES.SEO_KEYWORDS]: "SEO Keywords",
  [CONTENT_TYPES.SEO_OG_TITLE]: "Open Graph Title",
  [CONTENT_TYPES.SEO_OG_DESCRIPTION]: "Open Graph Description",
  [CONTENT_TYPES.SEO_SCHEMA_DESCRIPTION]: "Schema Description",
  [CONTENT_TYPES.SEO_IMAGE]: "SEO / Social Share Image",

  [CONTENT_TYPES.EMAIL_SUBJECT]: "Email Subject",
  [CONTENT_TYPES.EMAIL_PREHEADER]: "Email Preheader",
  [CONTENT_TYPES.EMAIL_HEADING]: "Email Heading",
  [CONTENT_TYPES.EMAIL_BODY]: "Email Body",
  [CONTENT_TYPES.EMAIL_BUTTON_LABEL]: "Email Button Label",
  [CONTENT_TYPES.EMAIL_FOOTER]: "Email Footer",
  [CONTENT_TYPES.EMAIL_IMAGE]: "Email Image",

  [CONTENT_TYPES.ADMIN_HELP_TEXT]: "Admin Help Text",
  [CONTENT_TYPES.ADMIN_WARNING]: "Admin Warning",
  [CONTENT_TYPES.ADMIN_SETUP_PROMPT]: "Admin Setup Prompt",

  [CONTENT_TYPES.LEGAL_TEXT]: "Legal Text",
  [CONTENT_TYPES.TRUST_TEXT]: "Trust Text",

  [CONTENT_TYPES.VOCABULARY]: "Vocabulary",

  [CONTENT_TYPES.IMAGE]: "Image",
  [CONTENT_TYPES.IMAGE_ALT_TEXT]: "Image Alt Text",
  [CONTENT_TYPES.IMAGE_CAPTION]: "Image Caption",
  [CONTENT_TYPES.IMAGE_TITLE]: "Image Title",
  [CONTENT_TYPES.IMAGE_DESCRIPTION]: "Image Description",
  [CONTENT_TYPES.ICON_LABEL]: "Icon Label",
  [CONTENT_TYPES.MEDIA_BLOCK]: "Media Block",
};